/** /settings/companion-pair - generate a one-time pairing token for
 *  the Memso Android Companion App and manage paired devices. The
 *  token is shown exactly once; the server stores only its hash. */
import { useCallback, useEffect, useState } from "react";
import { Smartphone, Trash2, Copy, Check } from "lucide-react";
import { api } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";

interface Device {
  id: string;
  device_name: string;
  paired_at: string;
  last_sync_at: string | null;
}

export function CompanionPair() {
  const [name, setName] = useState("Android phone");
  const [token, setToken] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api
      .companionDevices()
      .then((d) => setDevices(d.devices))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pair = async () => {
    setBusy(true);
    setError(null);
    setToken(null);
    try {
      const r = await api.companionPair(name.trim() || "Android phone");
      setToken(r.token);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Pairing failed.");
    } finally {
      setBusy(false);
    }
  };

  const unpair = async (id: string) => {
    setBusy(true);
    try {
      await api.companionUnpair(id);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <div className="flex items-center gap-2">
        <Smartphone className="w-5 h-5 text-sky-500" />
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          Pair the Memso Companion
        </h1>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Generate a one-time token, then enter it in the Memso app on
        your Android phone. The token is shown once and stored only as
        a hash. The phone uses it to sync captures over HTTPS.
      </p>

      <TeachingBanner id="companion_pair_overview" />

      {error && (
        <div className="text-sm text-rose-600 dark:text-rose-300">{error}</div>
      )}

      <div className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-3">
        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
          Device name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm font-normal text-slate-900 dark:text-slate-100"
          />
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={pair}
          className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50"
        >
          Generate pairing token
        </button>
        {token && (
          <div className="rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800/50 p-3 space-y-1">
            <div className="text-[11px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-300">
              Copy this now (shown once)
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all text-xs text-slate-800 dark:text-slate-100">
                {token}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(token);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                }}
                className="shrink-0 inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-1 text-xs font-semibold"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Paired devices
        </h2>
        {devices.length === 0 ? (
          <p className="text-sm text-slate-400">No devices paired yet.</p>
        ) : (
          <ul className="space-y-2">
            {devices.map((d) => (
              <li
                key={d.id}
                className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
                    {d.device_name}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    Paired {new Date(d.paired_at).toLocaleString()} ·{" "}
                    {d.last_sync_at
                      ? `last sync ${new Date(d.last_sync_at).toLocaleString()}`
                      : "never synced"}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => unpair(d.id)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Unpair
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
